import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AuthService } from './auth.service';
import { environment } from 'src/environments/environment';
import {
  BehaviorSubject, Observable, Subject,
} from 'rxjs';
import { EntityService } from './entity.service';
import { filter } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class UsersService {
  private _usersData$ = new BehaviorSubject<any>(null);
  public readonly usersData$ = this._usersData$.asObservable();

  private _selectedUserData$ = new BehaviorSubject<any>(null);
  public readonly selectedUserData$ = this._selectedUserData$.asObservable();

  private _userSearchResults$ = new BehaviorSubject<any>(null);
  public readonly userSearchResults$ = this._userSearchResults$.asObservable();

  private _reloadLogo$ = new Subject<void>();
  public readonly reloadLogo$ = this._reloadLogo$.asObservable();

  private _savedStaffFilter: any = null;

  user: any;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private entityService: EntityService,
  ) {
    this.authService.user$.subscribe((user: any) => (this.user = user));
    this.authService.loggedIn$.pipe(
      filter(loggedIn => !loggedIn)
    ).subscribe(() => this.clearSavedStaffFilter());
  }

  saveStaffFilter(filterState: any) {
    this._savedStaffFilter = filterState;
  }

  getSavedStaffFilter(): any {
    return this._savedStaffFilter;
  }

  clearSavedStaffFilter() {
    this._savedStaffFilter = null;
  }

  triggerLogoReload() {
    this._reloadLogo$.next();
  }

  getUserById = (userId: number, additionalParameters?: any) => {
    return this.http
      .get(`${environment.expressUrl}/users/${userId}`, {
        headers: { 'Content-Type': 'application/vnd.api+json' },
        params: { loginName: this.user.loginName, ...additionalParameters },
      })
      .subscribe({
        next: (response: any) => {
          this._selectedUserData$.next(response.data);
        },
        error: (error) => {
          console.log('error', error);
        },
      });
  };

  setUserLogo(
    loginName: string,
    filePath: string,
    fileName: string,
  ): Observable<any> {
    const body = {
      loginName,
      filePath,
      fileName,
    };
    return this.http.post(
      `${environment.expressUrl}/users/${this.entityService.user.id}/avatar`,
      body,
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    );
  }

  getUserLogo(loginName: string, userId: number): Observable<any> {
    return this.http.get(`${environment.expressUrl}/users/${userId}/avatar`, {
      headers: { 'Content-Type': 'application/vnd.api+json' },
      params: { loginName },
    });
  }

  removeUserLogo(loginName: string, userId: number): Observable<any> {
    const body = { loginName };

    return this.http.delete(`${environment.expressUrl}/users/${userId}/avatar`,
      {
        headers: { 'Content-Type': 'application/vnd.api+json' },
        body,
      });
  }

  clearSelectedUser = () => {
    this._selectedUserData$.next(null);
  };

  getUsers = ({
    userNameFilter,
    activeFlag = 1,
    sortColumn = 'tag',
    sortDirection = 'ASC',
    limit = 0,
    offset = 0,
    includedAssociations,
    showAssignedToMe,
    roleAccessLevels,
    customFlag,
    buildingIds,
  }: {
    userNameFilter: string;
    activeFlag: 1 | 0 | null;
    sortColumn: string;
    sortDirection: 'ASC' | 'DESC';
    limit?: number;
    offset?: number;
    includedAssociations: string;
    showAssignedToMe?: number;
    roleAccessLevels?: string;
    customFlag?: number;
    buildingIds?: number[];
  }) => {
    const params: any = {
      loginName: this.user.loginName,
      activeFlag,
      includedAssociations,
    };

    if (userNameFilter) {
      params.userNameFilter = userNameFilter;
    }

    if (limit !== 0) {
      params.limit = limit;
      params.offset = offset;
    }

    if (sortColumn) {
      params.sortColumnName = sortColumn;
      params.sortColumnDirection = sortDirection;
    }

    // Add new filter parameters
    if (showAssignedToMe !== undefined) {
      params.showAssignedToMe = showAssignedToMe;
    }

    if (roleAccessLevels && roleAccessLevels.length > 0) {
      params.roleAccessLevels = roleAccessLevels;
    }

    if (customFlag !== undefined) {
      params.customFlag = customFlag;
    }

    if (buildingIds !== undefined && buildingIds.length > 0) {
      params.buildingIds = buildingIds.join(',');
    }

    return this.http
      .get(`${environment.expressUrl}/users`, {
        headers: { 'Content-Type': 'application/vnd.api+json' },
        params,
      })
      .subscribe({
        next: (response: any) => {
          this._usersData$.next(response.data);
        },
        error: (error) => {
          console.log('error', error);
          // If buildingIds parameter caused a 422 error, return empty data to trigger fallback
          if (error.status === 422 && params.buildingIds) {
            console.log('BuildingIds parameter not supported, using fallback filtering');
            this._usersData$.next({ rows: [] });
          } else {
            this._usersData$.next(error);
          }
        },
      });
  };

  getUsersFilteredByName = (searchObject: any) => {
    return this.http
      .get(`${environment.expressUrl}/users`, {
        headers: { 'Content-Type': 'application/vnd.api+json' },
        params: {
          loginName: this.user.loginName,
          limit: 25,
          activeFlag: searchObject.showActiveItems ? 1 : 0,
          userNameFilter: searchObject.searchText,
          includedAssociations: 'PrimaryBuilding, PrimaryPosition',
        },
      })
      .subscribe({
        next: (response: any) => {
          this._usersData$.next(response.data);
        },
        error: (error) => {
          this._usersData$.next(error);
          console.log('error', error);
        },
      });
  };

  // TODO: add licenceAndCertsArr to the post request once the endpoint is available
  addUser = (requestBody: any) => {
    return this.http.post(
      `${environment.expressUrl}/users`,
      {
        loginName: this.user.loginName,
        ...requestBody,
      },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    );
  };

  // TODO: add license and certs once endpoint is available
  updateUser = (requestBody: any, userId: number) => {
    // TODO: maybe add another permission check here, as we are primarily disabling fields in the UI
    return this.http.put(
      `${environment.expressUrl}/users/${userId}`,
      {
        loginName: this.user.loginName,
        ...requestBody,
      },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    );
  };

  bulkAssignRequirements = (userIds: number[], requirementData: any) => {
    const requestBody = {
      loginName: this.user.loginName,
      userIds: userIds,
      addRequirements: [
        {
          requirementId: requirementData.requirementId,
          requiredValue: requirementData.requiredValue,
        },
      ],
    };

    return this.http.put(
      `${environment.expressUrl}/users/bulk-requirements`,
      requestBody,
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    );
  };

  getInactiveUsers = (
    includedAssociations: string, limit = 25, offset = 0,
  ) => {
    return this.http
      .get(`${environment.expressUrl}/users`, {
        headers: { 'Content-Type': 'application/vnd.api+json' },
        params: {
          loginName: this.user.loginName,
          limit: limit,
          offset: offset,
          activeFlag: 0,
          includedAssociations: includedAssociations,
        },
      })
      .subscribe({
        next: (response: any) => {
          this._usersData$.next(response.data);
        },
        error: (error) => {
          console.log('error', error);
        },
      });
  };

  searchUsers = (searchText: string) => {
    if (!searchText?.trim()) {
      this._userSearchResults$.next(null);
      return;
    }
    const savedFilter = this._savedStaffFilter;
    const activeFlag = savedFilter?.showInactiveMembers ? 0 : 1;

    const roleAccessLevels: string[] = [];
    if (savedFilter?.showBasicMembers) roleAccessLevels.push('BASIC');
    if (savedFilter?.showDefaultAdministrators) roleAccessLevels.push('MANAGE_ALL_AUTHORITY');
    if (savedFilter?.showDefaultSupervisors) roleAccessLevels.push('MANAGE_SECONDARY_AUTHORITY');
    if (savedFilter?.showCustomRoles) roleAccessLevels.push('CUSTOM');

    const params: any = {
      loginName: this.user.loginName,
      limit: 25,
      activeFlag,
      userNameFilter: searchText,
      sortColumnName: 'firstName',
      sortColumnDirection: 'ASC',
      includedAssociations: 'PrimaryBuilding, PrimaryPosition',
    };

    if (roleAccessLevels.length > 0) {
      params.roleAccessLevels = roleAccessLevels.join(',');
    }

    if (savedFilter?.showOnlyAssignedToMe) {
      params.showAssignedToMe = 1;
    }

    return this.http
      .get(`${environment.expressUrl}/users`, {
        headers: { 'Content-Type': 'application/vnd.api+json' },
        params,
      })
      .subscribe({
        next: (response: any) => {
          this._userSearchResults$.next(response.data);
        },
        error: (error) => {
          console.log('error', error);
          this._userSearchResults$.next(null);
        },
      });
  };

  clearSearchResults = () => {
    this._userSearchResults$.next(null);
  };

  getPagination = (pageDirection: any) => {
    let links = '';
    let pagination = {
      curPage: 0,
      totalItems: 0,
      totalPages: 0,
    };
    this.usersData$.subscribe((data) => {
      links = data.links;
      pagination = data.pagination;
    });

    if (links[pageDirection] === '') {
      return;
    }
    return this.http
      .get(`${environment.expressUrl}/users/` + links[pageDirection], { headers: { 'Content-Type': 'application/vnd.api+json' } })
      .subscribe({
        next: (response: any) => {
          this._usersData$.next(response.data);
          console.log('response', response);
        },
        error: (error) => {
          console.log('error', error);
        },
      });
  };
}
